import utils from '../utils';
import { USER_BASE_WAGE_KEY } from '../constants';
import gettext from '../lang/gettext';

export default function setUserWages(user, personasDict) {
  user.historicalWages = [];
  user.historicalWagesPerPosition = {};
  user.salaryWages = [];

  if (!('wages' in user)) {
    user.wagesList = [];
    user.wages = {};
  }

  if (user.positions) {
    user.positions.forEach((position) => {
      if (!(position.id in user.wages)) {
        user.wages[position.id] = [];
      }
    });
  }

  const _mapWage = (wage, positionId) => ({
    ...wage,
    fromDate: utils.getRealDate(wage.dateEffective),
    regularRate: Number(wage.regularRate),
    userId: user.id,
    positionId: positionId ? utils.getId(positionId) : undefined,
    position: positionId ? personasDict[positionId] : undefined,
    locationId: wage.locationId,
    location: personasDict[wage.locationId],
  });

  user.wagesList = Object.keys(user.wages)
    .filter((positionId) => positionId !== USER_BASE_WAGE_KEY)
    .map((positionId) => {
      user.wages[positionId] = user.wages[positionId].map((wage) => _mapWage(wage, positionId));
      user.wages[positionId] = utils.sortBy(user.wages[positionId], 'fromDate');

      const position = personasDict[positionId];

      return {
        position,
        positionId,
        wages: user.wages[positionId],
      };
    });

  user.baseWages = (user.wages[USER_BASE_WAGE_KEY] || []).map((w) => _mapWage(w));

  if (user.wages[USER_BASE_WAGE_KEY]) {
    user.wages[USER_BASE_WAGE_KEY] = utils.sortBy(user.baseWages, 'fromDate');
  }

  user.wagesList.forEach((wageGroup) => {
    const groupWages = utils.sortBy(
      [
        ...user.baseWages.map((w) => ({ ...w, baseWage: true })),
        ...(wageGroup.position?.wages ? wageGroup.position.wages.filter((w) => w.id) : []),
        ...wageGroup.wages.filter((w) => w.id),
      ],
      'fromDate'
    );

    user.salaryWages = user.salaryWages.concat(groupWages.filter((w) => w.isSalary));
    const groupHourlyWages = groupWages.filter((w) => !w.isSalary);

    const firstPositionWageIndex = groupHourlyWages.findIndex((w) => !w.userId);
    const firstUserPositionWageIndex = groupHourlyWages.findIndex((w) => w.userId && w.positionId);
    const relevantWages = groupHourlyWages.filter((w, i) => {
      if (w.userId && w.positionId) {
        return true;
      }

      if (!w.userId) {
        // position wage
        return firstUserPositionWageIndex === -1 || i < firstUserPositionWageIndex;
      }

      if (firstUserPositionWageIndex === -1) {
        // no user position wages
        return firstPositionWageIndex === -1 || i < firstPositionWageIndex;
      }

      if (firstPositionWageIndex === -1) {
        // no position wages
        return firstUserPositionWageIndex === -1 || i < firstUserPositionWageIndex;
      }

      return i < Math.min(firstUserPositionWageIndex, firstPositionWageIndex);
    });
    let firstFutureIndex = relevantWages.findIndex((w) => w.fromDate > utils.getNowDate());
    if (firstFutureIndex === -1) {
      firstFutureIndex = relevantWages.length;
    }

    const groupWagesMapped = relevantWages.map((w) => ({ ...wageGroup, wage: w }));

    user.historicalWagesPerPosition[wageGroup.positionId] = groupWagesMapped.filter(
      (w, i) => i < firstFutureIndex - 1
    );
    user.historicalWages = user.historicalWages.concat(
      user.historicalWagesPerPosition[wageGroup.positionId]
    );

    wageGroup.groupWages = relevantWages;
  });

  user.salaryWages = utils.sortBy(user.salaryWages, 'fromDate');
  user.currentSalary = getCurrentSalary(user.wages);

  if (user.currentSalary) {
    const indexOfCurrentSalary = user.salaryWages.findIndex((s) => s === user.currentSalary);
    user.historicalWages = user.historicalWages.concat(
      user.salaryWages
        .filter((w, i) => i < indexOfCurrentSalary)
        .map((wage) => ({ position: wage.position, wage }))
    );
  } else {
    user.historicalWages = user.historicalWages.concat(
      user.salaryWages
        .filter((w) => w.fromDate < utils.getNowDate())
        .map((wage) => ({ position: wage.position, wage }))
    );
  }

  if (user.currentSalary) {
    user.currentWageType = gettext('Salary');
  } else {
    user.currentWageType = user.wagesList.length ? gettext('Hourly') : '';
  }

  user.historicalWages = utils.sortBy(
    utils.uniq(user.historicalWages, (w) => w.wage.id),
    'wage.fromDate'
  );
}

function getCurrentSalary(userWages) {
  const all = Object.values(userWages).reduce((allWages, wages) => allWages.concat(wages), []);

  let currentSalary;
  let effectiveDate;
  const now = utils.getNowDate();

  all.forEach((wage) => {
    if (wage.fromDate <= now && (!effectiveDate || effectiveDate < wage.fromDate)) {
      currentSalary = wage;
      effectiveDate = wage.fromDate;
    }
  });

  return currentSalary && currentSalary.isSalary ? currentSalary : null;
}
